import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  UserCog,
  DollarSign,
  Cpu,
  Heart,
  KeyRound,
  Flag,
  HelpCircle,
  BarChart3,
  Building2,
  Sliders,
  ShieldCheck,
  ClipboardList,
} from "lucide-react";
import { isLocalAuth } from "../config/auth";
import ImpersonationSection from "../components/admin/ImpersonationSection";
import SubscriptionTierSection from "../components/admin/SubscriptionTierSection";
import ModelPricingSection from "../components/admin/ModelPricingSection";
import CostTrackingSection from "../components/admin/CostTrackingSection";
import SystemHealthSection from "../components/admin/SystemHealthSection";
import FeatureFlagsSection from "../components/admin/FeatureFlagsSection";
import SupportRequestSection from "../components/admin/SupportRequestSection";
import OrganizationCostsSection from "../components/admin/OrganizationCostsSection";
import OrgQuotaOverridesSection from "../components/admin/OrgQuotaOverridesSection";
import AdminPasswordResetSection from "../components/admin/AdminPasswordResetSection";
import AdminRoleManagementSection from "../components/admin/AdminRoleManagementSection";
import LeadQuestionnaireSection from "../components/admin/LeadQuestionnaireSection";

const AdminPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/repositories" replace />;
  }

  const sections = [
    {
      id: "support-requests",
      label: "Support Requests",
      icon: HelpCircle,
      component: SupportRequestSection,
    },
    {
      id: "lead-questionnaires",
      label: "Lead Questionnaires",
      icon: ClipboardList,
      component: LeadQuestionnaireSection,
    },
    {
      id: "subscription-tiers",
      label: "Subscription Tiers",
      icon: DollarSign,
      component: SubscriptionTierSection,
    },
    {
      id: "model-pricing",
      label: "Model Pricing",
      icon: Cpu,
      component: ModelPricingSection,
    },
    {
      id: "cost-tracking",
      label: "Cost Tracking",
      icon: BarChart3,
      component: CostTrackingSection,
    },
    {
      id: "organization-costs",
      label: "Organization Costs",
      icon: Building2,
      component: OrganizationCostsSection,
    },
    {
      id: "quota-overrides",
      label: "Quota Overrides",
      icon: Sliders,
      component: OrgQuotaOverridesSection,
    },
    {
      id: "features",
      label: "Feature Flags",
      icon: Flag,
      component: FeatureFlagsSection,
    },
    {
      id: "impersonation",
      label: "Impersonation",
      icon: UserCog,
      component: ImpersonationSection,
    },
    {
      id: "admin-roles",
      label: "Admin Roles",
      icon: ShieldCheck,
      component: AdminRoleManagementSection,
    },
    ...(isLocalAuth
      ? [
          {
            id: "password-reset",
            label: "Password Reset",
            icon: KeyRound,
            component: AdminPasswordResetSection,
          },
        ]
      : []),
    {
      id: "system-health",
      label: "System Health",
      icon: Heart,
      component: SystemHealthSection,
    },
  ];

  // Extract current section from URL or default to first section in the list
  const currentSection =
    location.pathname.split("/admin/")[1] || sections[0].id;

  const handleSectionChange = (sectionId: string) => {
    navigate(`/admin/${sectionId}`);
  };

  const currentSectionData = sections.find(
    (section) => section.id === currentSection,
  );
  const CurrentSectionComponent =
    currentSectionData?.component || sections[0].component;

  return (
    <div className="min-h-screen bg-ui-background-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-3">
            <h1 className="text-3xl font-semibold text-ui-text-primary">
              Administration
            </h1>
          </div>
          <p className="text-ui-text-secondary text-lg">
            Perform system administration tasks
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="bg-ui-background-secondary rounded-xl border border-ui-border-primary shadow-sm sticky top-8">
              <div className="p-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = section.id === currentSection;

                  return (
                    <button
                      key={section.id}
                      onClick={() => handleSectionChange(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                        isActive
                          ? "bg-brand-primary text-black shadow-sm"
                          : "text-ui-text-secondary hover:text-ui-text-primary hover:bg-ui-background-tertiary"
                      }`}
                    >
                      <Icon size={18} />
                      {section.label}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <div className="bg-ui-background-secondary rounded-xl border border-ui-border-primary shadow-sm overflow-hidden">
              <CurrentSectionComponent />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
